import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import { Page } from 'react-pdf';
import Block from './Block';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import { boxShadows } from '../theme';
import { IBlock, IGroup, ISignatureSettings } from '../store/types';
import GroupBox from './GroupBox';
import { IEnvelopeState } from '../store/reducers/envelope';
import { colors } from '@skyslope/mache';
import NextButton from './NextButton';
import { makeStyles } from '@material-ui/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery';
import { MD_MEDIA_QUERY, SKELETON_WIDTH, SKELETON_HEIGHT } from './../common/constants';
import VisibilitySensor from 'react-visibility-sensor';
import PageAnnotations from './PageAnnotations';

const useStyles = makeStyles((theme: Theme) => ({
  pageWrapper: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(6),
  },
  pageNumber: {
    fontSize: '14px',
    color: colors.grey[800],
    paddingLeft: theme.spacing(),
    paddingBottom: theme.spacing(),
  },
  pageContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  pageFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '8px',
    color: colors.grey[800],
  },
  footerDocument: {
    whiteSpace: 'nowrap',
    width: '65%',
    textOverflow: 'ellipsis',
    overflow: 'auto',
  },
  responsiveDocument: {
    '@global canvas': {
      width: '100% !important',
      height: 'auto !important',
    },
    position: 'relative',
  },
  visibilityPixel: {
    position: 'absolute',
    left: '50%',
    top: 200,
    zIndex: 0,
    width: 1,
    height: 1,
  },
  pageSkeleton: {
    boxShadow: boxShadows.main,
    backgroundColor: 'rgba(255,255,255,0.5)',
    position: 'relative',
    height: 0,
  },
  '@global': {
    '.linkAnnotation': (props: IProps) => ({
      width: `${props.isMobile ? '50px !important' : ''}`,
      height: `${props.isMobile ? '5px !important' : ''}`,
      top: `${props.isMobile ? '0px !important' : ''}`,
      left: `${props.isMobile ? '0px !important' : ''}`,
    }),
  },
}));

export interface IAnnotation {
  id: string;
  width: number;
  height: number;
  x: number;
  y: number;
  url: string;
}

interface IProps {
  documentIndex: number;
  pageNumber: number;
  totalPages: number;
  totalDocuments: number;
  documentName: string;
  documentPages: number;
  blocks: { number: IBlock } | {};
  zoom: number;
  fullPage: number;
  clickSignature: Function;
  updateBlock: Function;
  groups?: {
    [pageIndex: number]: IGroup;
  };
  envelope: IEnvelopeState;
  signingExperienceBlocks: IBlock[];
  signingExperienceComplete: boolean;
  currentSelectedBlockIndex: number | null;
  documentId: string;
  allBlocksComplete: boolean;
  scrollToNextBlock: () => void;
  tabToBlock: (nextIndex: number) => void;
  onLoadSuccess?: (page: any, fullPage: number) => void;
  pageDimensions: { width?: number; height?: number };
  visiblePage: number;
  setVisiblePage: (fullPage: number, documentId: string, documentName: string) => void;
  pageScale: number;
  setPageScale: (fullPage: number, pageScale: number) => void;
  signatureSettings?: ISignatureSettings;
  scrollToBlock: (blockElement: HTMLDivElement, fullPage: number) => void;
  setBlocksInGroupToComplete: (blocks: IBlock[]) => void;
  isMobile: boolean;
}

const PdfPage = (props: IProps) => {
  // The reason we use isMobile is on phones in portrait mode, the media query won't trigger. This
  // also prevents being fully zoomed out while on small desktop windows.
  const {
    blocks,
    groups,
    envelope,
    signingExperienceBlocks,
    documentId,
    currentSelectedBlockIndex,
    pageNumber,
    signingExperienceComplete,
    zoom,
    pageScale,
    setPageScale,
    onLoadSuccess,
    fullPage,
    pageDimensions,
    setBlocksInGroupToComplete,
  } = props;
  const screenWidthIsLessThanOrEqualToMdBreakpoint = useMediaQuery(MD_MEDIA_QUERY);
  const classes = useStyles(props);
  const [keyIndex, setKey] = useState(0);
  const pageRef = React.useRef<HTMLDivElement | null>(null);
  const [pageAnnotations, setPageAnnotations] = React.useState<IAnnotation[] | undefined>();

  const setVisiblePage = () => {
    props.setVisiblePage(props.fullPage, props.documentId, props.documentName);
  };

  const recalculatePageScale = () => {
    if (pageRef.current && props.pageDimensions.width) {
      const scale = pageRef.current!.clientWidth / props.pageDimensions.width!;
      if (scale !== pageScale) {
        setPageScale(fullPage, scale);
      }
    }
  };

  const handlePageLoadSuccess = (page: any) => {
    onLoadSuccess!(page, fullPage);
  };

  React.useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;
    const resizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => recalculatePageScale(), 250);
    };
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [pageRef.current]);

  useEffect(() => recalculatePageScale());

  useEffect(() => {
    setKey(keyIndex + 1);
  }, [blocks]);

  const scrollToBlock = (blockElement: HTMLDivElement) => {
    props.scrollToBlock(blockElement, props.fullPage);
  };

  // Map object to keep track of how many of each type of block we have
  const blockCounts = {};
  let selectedBlock: IBlock | undefined = undefined;
  if (currentSelectedBlockIndex !== null) {
    if (
      signingExperienceBlocks[currentSelectedBlockIndex].pageNumber === pageNumber &&
      signingExperienceBlocks[currentSelectedBlockIndex].documentId === documentId
    ) {
      let temps = signingExperienceBlocks[currentSelectedBlockIndex];
      selectedBlock = blocks[temps.blockId];
    }
  }

  const getBlockIndexFromSigningExperience = (block: IBlock) => {
    return props.signingExperienceBlocks.findIndex(b => b.blockId === block.blockId);
  };

  const isPageFocused =
    currentSelectedBlockIndex !== null && signingExperienceBlocks[currentSelectedBlockIndex].pageNumber === pageNumber;

  const pageFooter = () => {
    return screenWidthIsLessThanOrEqualToMdBreakpoint ? (
      <div className={classes.pageFooter}>
        <div className={classes.footerDocument}>{`Doc ${props.documentIndex + 1}/${props.totalDocuments}: ${
          props.documentName
        }`}</div>
        <div>{`Page ${props.pageNumber + 1}/${props.totalPages}`}</div>
      </div>
    ) : (
      ''
    );
  };

  const pageClass = screenWidthIsLessThanOrEqualToMdBreakpoint
    ? cx(classes.page, classes.responsiveDocument)
    : classes.page;
  const selector = props.fullPage.toString();

  const renderPdf = props.fullPage < props.visiblePage + 2 && props.fullPage > props.visiblePage - 2;

  const width = screenWidthIsLessThanOrEqualToMdBreakpoint
    ? '100%'
    : pageDimensions.width
    ? pageDimensions.width * zoom
    : SKELETON_WIDTH * zoom;

  const aspectRatio = pageDimensions.height
    ? (props.pageDimensions.height! / props.pageDimensions.width!) * 100
    : (SKELETON_HEIGHT / SKELETON_WIDTH) * 100;

  return (
    <div className={classes.pageWrapper} id={`page-${props.fullPage}`} style={{ width }}>
      {screenWidthIsLessThanOrEqualToMdBreakpoint ? (
        ''
      ) : (
        <div className={classes.pageNumber}>{`Page ${props.pageNumber + 1}/${props.documentPages}`}</div>
      )}

      <div className={classes.pageSkeleton} style={{ width, paddingTop: `${aspectRatio}%` }}>
        <div className={classes.pageContainer} id={`p-${selector}`}>
          <VisibilitySensor onChange={(isVisible: boolean) => isVisible && setVisiblePage()}>
            <div className={classes.visibilityPixel} />
          </VisibilitySensor>
          {isPageFocused || (currentSelectedBlockIndex === null && pageNumber === 0 && !props.allBlocksComplete)
            ? (selectedBlock || pageNumber === 0) &&
              !screenWidthIsLessThanOrEqualToMdBreakpoint &&
              props.envelope.envelope!.signer.signingStatus !== 'Complete' && (
                <NextButton
                  onClick={props.scrollToNextBlock}
                  selectedBlock={selectedBlock}
                  zoom={zoom}
                  currentSelectedBlockIndex={currentSelectedBlockIndex}
                  documentIndex={props.documentIndex}
                  documentId={documentId}
                  pageDimensions={props.pageDimensions}
                />
              )
            : ''}
          {renderPdf && (
            <>
              <Page
                inputRef={ref => (pageRef.current = ref)}
                pageNumber={props.pageNumber + 1}
                scale={zoom}
                className={pageClass}
                loading=""
                renderInteractiveForms={false}
                renderTextLayer={false}
                onLoadSuccess={fullPage === 1 || !pageDimensions.height ? handlePageLoadSuccess : undefined}
              />
              <PageAnnotations
                pageAnnotations={pageAnnotations}
                isMobile={screenWidthIsLessThanOrEqualToMdBreakpoint}
              />
            </>
          )}
          {groups && envelope.envelope!.status !== 'Completed'
            ? Object.keys(groups).map((groupId, index) => {
                const group = groups[groupId];
                const blocks = group.blockIds.map((blockId: string) => props.blocks[blockId]);
                const firstBlockInGroup = blocks[0];
                const tabIndex = getBlockIndexFromSigningExperience(firstBlockInGroup);
                return (
                  <GroupBox
                    blocks={blocks}
                    zoom={zoom}
                    clickSignature={props.clickSignature}
                    updateBlock={props.updateBlock}
                    group={group}
                    key={index}
                    tabIndex={tabIndex}
                    currentSelectedBlockIndex={currentSelectedBlockIndex}
                    signingExperienceBlocks={signingExperienceBlocks}
                    signingExperienceComplete={signingExperienceComplete}
                    envelopeStatus={envelope.envelope!.status}
                    tabToBlock={props.tabToBlock}
                    pageDimensions={props.pageDimensions}
                    scrollToBlock={scrollToBlock}
                    setBlocksInGroupToComplete={setBlocksInGroupToComplete}
                  />
                );
              })
            : ''}
          {Object.keys(blocks)
            .filter(blockId => !blocks[blockId].groupId)
            .map((blockId: string, index: number) => (
              <Block
                key={index}
                keyIndex={keyIndex}
                block={blocks[blockId]}
                index={(blockCounts[blocks[blockId].blockType] += 1)}
                zoom={zoom}
                clickSignature={props.clickSignature.bind(null, blockId)}
                updateBlock={props.updateBlock.bind(null, blockId)}
                signer={envelope.envelope!.signer}
                tabIndex={getBlockIndexFromSigningExperience(blocks[blockId])}
                currentSelectedBlockIndex={props.currentSelectedBlockIndex}
                signingExperienceComplete={signingExperienceComplete}
                tabToBlock={props.tabToBlock}
                pageDimensions={props.pageDimensions}
                pageScale={pageScale}
                signatureSettings={props.signatureSettings}
                scrollToBlock={scrollToBlock}
                dateFormatPreference={envelope.envelope!.config.dateFormat}
              />
            ))}
        </div>
      </div>
      {pageFooter()}
    </div>
  );
};

export default PdfPage;
